import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { AppShell } from "./components/AppShell.js";
import { SignalDetailModal } from "./components/signal-detail/SignalDetailModal.js";
import { FIRST_TOPIC_SLUG } from "./content/wiki/registry.js";
import { login, type PublicUser } from "./lib/api.js";
import {
  type ScreenSearch,
  type SignalsSearch,
  type StrategyLabSearch,
  type SymbolSearch,
  screenSearchSchema,
  signalsSearchSchema,
  strategyLabSearchSchema,
  symbolSearchSchema,
} from "./lib/search-params.js";
import { forgetSession, loadSession } from "./lib/session-guard.js";
import { type TradesSearch, tradesSearchSchema } from "./lib/trades-search.js";
import { HealthPage } from "./routes/health.js";
import { LoginPage } from "./routes/login.js";
import { SettingsPage } from "./routes/settings.js";

/**
 * The heavy pages load on demand.
 *
 * Measured 2026-08-29 against production: the single bundle was 1,128KB, and
 * because every page was imported statically here, recharts and
 * lightweight-charts downloaded before the login form could paint. Login and
 * Health stay eager — Login is the first thing an unauthenticated visitor
 * sees, and Health is a table.
 */
const ScreenPage = lazyRouteComponent(() => import("./routes/screen.js"), "ScreenPage");
const FactorLabPage = lazyRouteComponent(() => import("./routes/factor-lab.js"), "FactorLabPage");
const StrategyLabPage = lazyRouteComponent(
  () => import("./routes/strategy-lab.js"),
  "StrategyLabPage",
);
const SymbolPage = lazyRouteComponent(() => import("./routes/symbol.$symbol.js"), "SymbolPage");
const SignalsPage = lazyRouteComponent(() => import("./routes/signals.js"), "SignalsPage");
const SignalDetailPage = lazyRouteComponent(
  () => import("./routes/signal.$signalId.js"),
  "SignalDetailPage",
);
const DocsPage = lazyRouteComponent(() => import("./routes/docs.$slug.js"), "DocsPage");
const TradesPage = lazyRouteComponent(() => import("./routes/trades.js"), "TradesPage");

type Ctx = { user: PublicUser | null };

const rootRoute = createRootRoute<Ctx>({ component: Outlet });

/**
 * Shown while a navigation is still resolving.
 *
 * It doubles as the Suspense boundary the lazy page components need, which is
 * why it is configured on the router rather than per route.
 */
function RoutePending() {
  return <p className="px-8 py-6 text-sm text-text-muted">Loading…</p>;
}

function LoginRoute() {
  const router = useRouter();
  return (
    <LoginPage
      onSubmit={async (email, password) => {
        const result = await login(email, password);
        if (result.ok) {
          // The guard below cached the `null` that sent us here. Dropping it
          // forces the next read to ask the server again; without this the
          // redirect fires straight back to /login.
          forgetSession();
          await router.invalidate();
          await router.navigate({ to: "/" });
        }
        return result;
      }}
    />
  );
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginRoute,
});

/**
 * Everything behind the guard hangs off this layout route.
 *
 * One `beforeLoad` rather than six: an authenticated dashboard with four
 * users does not need six copies of the same redirect, and six copies is how
 * one of them gets forgotten.
 *
 * The session goes through the query cache because the router re-runs
 * `beforeLoad` on every navigation and does not cache its result. Measured
 * 2026-08-29 against production: that put an uncached 236-500ms round trip in
 * front of every sort click and every keystroke, with the UI showing nothing
 * until it resolved. Sign-out reloads the page, so it clears this by itself.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async () => {
    const user = await loadSession();
    if (!user) throw redirect({ to: "/login" });
    return { user };
  },
  component: function App() {
    const { user } = appRoute.useRouteContext();
    return (
      <AppShell displayName={user.displayName}>
        <Outlet />
        {/* One instance, mounted in the layout rather than in AppShell: the
            modal is a route-level concern and AppShell is the chrome that
            wraps whatever a route renders. */}
        <SignalDetailModal />
      </AppShell>
    );
  },
});

/**
 * `/` is the screener.
 *
 * It used to be a placeholder that said the screener would land here in
 * Stage 3. Stage 3 landed, and the page stayed — so every login and every
 * bookmark of the bare host arrived at a paragraph about a stage that was
 * already built. A redirect rather than a duplicate mount: one route owns
 * `/screen`, including its search schema.
 */
const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/screen" });
  },
});

/*
 * Filter and sort changes push a history entry, so Back undoes one step.
 *
 * These three navigations were `replace: true` for a while. Measured
 * 2026-08-29 against production: typing eight characters into a filter box
 * left eight entries behind it, so leaving the page took nine presses of Back.
 * `useDebouncedSearchField` fixed that where it happened — a typed word is one
 * commit now, not one per keystroke — and `replace` cost more than it bought.
 * It reached every caller of `onSearchChange`, and the sort handlers on the
 * screen and the feed are callers too, so a header click could not be undone
 * and Back on a filtered page left the page rather than dropping the filter.
 */

const screenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/screen",
  validateSearch: (search: Record<string, unknown>): ScreenSearch =>
    screenSearchSchema.parse(search),
  component: function Screen() {
    const search = screenRoute.useSearch();
    const navigate = screenRoute.useNavigate();
    return (
      <ScreenPage
        search={search}
        onSearchChange={(next) =>
          navigate({ search: (prev: ScreenSearch) => ({ ...prev, ...next }) })
        }
      />
    );
  },
});

const factorLabRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/factor-lab",
  component: FactorLabPage,
});

/** Same navigate pattern as `/screen`: a control change is one history entry. */
const strategyLabRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/strategy-lab",
  validateSearch: (search: Record<string, unknown>): StrategyLabSearch =>
    strategyLabSearchSchema.parse(search),
  component: function StrategyLab() {
    const search = strategyLabRoute.useSearch();
    const navigate = strategyLabRoute.useNavigate();
    return (
      <StrategyLabPage
        search={search}
        onSearchChange={(next) =>
          navigate({ search: (prev: StrategyLabSearch) => ({ ...prev, ...next }) })
        }
      />
    );
  },
});

const symbolRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/symbol/$symbol",
  validateSearch: (search: Record<string, unknown>): SymbolSearch =>
    symbolSearchSchema.parse(search),
  component: function SymbolRoute() {
    const { symbol } = symbolRoute.useParams();
    const search = symbolRoute.useSearch();
    const navigate = symbolRoute.useNavigate();
    return (
      <SymbolPage
        symbol={symbol}
        search={search}
        onSearchChange={(next) =>
          navigate({ search: (prev: SymbolSearch) => ({ ...prev, ...next }) })
        }
      />
    );
  },
});

const signalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/signals",
  validateSearch: (search: Record<string, unknown>): SignalsSearch =>
    signalsSearchSchema.parse(search),
  component: function Signals() {
    const search = signalsRoute.useSearch();
    const navigate = signalsRoute.useNavigate();
    return (
      <SignalsPage
        search={search}
        onSearchChange={(next) =>
          navigate({ search: (prev: SignalsSearch) => ({ ...prev, ...next }) })
        }
      />
    );
  },
});

/**
 * Static segments match before dynamic ones, so `/signals` and
 * `/signals/$signalId` coexist. That guarantee is the kind of thing a router
 * upgrade changes quietly, which is why there is a test for it.
 */
const signalDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/signals/$signalId",
  component: function SignalDetail() {
    const { signalId } = signalDetailRoute.useParams();
    return <SignalDetailPage signalId={signalId} />;
  },
});

/**
 * `/docs` is the first topic rather than an index page.
 *
 * An index that lists eighteen titles is a page a reader passes through, and
 * the rail already lists all eighteen on every topic. Landing on the pipeline
 * topic starts them where the reading order starts.
 */
const docsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/docs",
  beforeLoad: () => {
    throw redirect({ to: "/docs/$slug", params: { slug: FIRST_TOPIC_SLUG } });
  },
});

const docsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/docs/$slug",
  component: function Docs() {
    const { slug } = docsRoute.useParams();
    return <DocsPage slug={slug} />;
  },
});

/**
 * Hand-entered trades and the exit watcher. The search carries a signal's
 * plan when the reader arrives from "Track this trade".
 */
const tradesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/trades",
  validateSearch: (search: Record<string, unknown>): TradesSearch =>
    tradesSearchSchema.parse(search),
  component: function Trades() {
    return <TradesPage search={tradesRoute.useSearch()} />;
  },
});

const healthRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/health",
  component: HealthPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    dashboardRoute,
    screenRoute,
    factorLabRoute,
    symbolRoute,
    signalsRoute,
    signalDetailRoute,
    tradesRoute,
    strategyLabRoute,
    docsIndexRoute,
    docsRoute,
    healthRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { user: null },
  defaultPendingComponent: RoutePending,
  // Long enough that a cached guard resolves without a flash, short enough
  // that a cold one does not look like a hang.
  defaultPendingMs: 200,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
